import type { ModuleKey, PermissionKey, RoleKey } from "@how/types";

export interface PermissionDefinition {
  readonly key: PermissionKey;
  readonly module: ModuleKey | null;
  readonly description: string;
  readonly isSensitive: boolean;
}

const p = (
  key: PermissionKey,
  module: ModuleKey | null,
  description: string,
  isSensitive = false,
): PermissionDefinition => ({ key, module, description, isSensitive });

/** Single source of truth for permissions. Changing this file requires `pnpm catalog:generate`. */
export const PERMISSIONS: readonly PermissionDefinition[] = [
  p(
    "settings.manage",
    "tenant_admin",
    "Manage church settings, branding, domains, MFA policy",
    true,
  ),
  // Owner decision of 2026-10-03: the church's time zone is the owner's to change (and DCX support's, inside a
  // read and write session that names it). Its own key, so a session can carry it without the rest of settings.
  p("settings.time_zone", "tenant_admin", "Change the church's time zone", true),
  // Single sign-in phase B (ADR 0067 R5): a church's own sign-in addresses decide where staff type their passwords.
  // Its own key, owner-only, so it stays the owner's even if other settings are ever shared with administrators; the
  // functions refuse any support session, and request_support_session refuses to grant it (ruling P3).
  // Check now on a sign-in address: the church owner (domains.manage_signin, two-step, no support session) or a DCX
  // super admin outside a support session. Any caller inside an active support session is refused.
  p(
    "domains.manage_signin",
    "tenant_admin",
    "Add or remove the church's own sign-in addresses and choose the preferred address",
    true,
  ),
  p("users.read", "tenant_admin", "View staff and member accounts"),
  p("users.manage", "tenant_admin", "Invite, suspend, remove accounts and assign roles", true),
  p("audit_logs.view", "tenant_admin", "View the church's audit log", true),
  p("people.read", "core_crm", "View people"),
  p("people.write", "core_crm", "Create and edit people"),
  p("people.export", "core_crm", "Export people", true),
  p("people.merge", "core_crm", "Merge duplicate people", true),
  p("households.manage", "core_crm", "Manage households"),
  p("notes.read", "core_crm", "Read notes"),
  p("notes.write", "core_crm", "Write notes"),
  p("notes.read_confidential", "core_crm", "Read notes marked confidential", true),
  p("people.read_minors", "core_crm", "See the records of children", true),
  p("tags.manage", "core_crm", "Manage tags"),
  p("people.import", "core_crm", "Import people from a file", true),
  // Care (Milestone 3 task 15, D13): a request has an owner and a status, unlike a note or a form submission.
  p("care.read", "core_crm", "See the care queue"),
  p("care.manage", "core_crm", "Take, hand on and close care requests"),
  // Sensitive since Milestone 5 B4 (ADR 0054): one of the three extras a custom role carries only on purpose (D13, D21).
  p("care.read_confidential", "core_crm", "Read care requests marked confidential", true),
  p("preferences.manage", "core_crm", "Edit someone else's communication preferences"),
  p("custom_fields.manage", "core_crm", "Manage custom fields"),
  p("website.read", "website", "View content drafts"),
  p("website.manage", "website", "Edit pages, navigation, blocks"),
  p("website.publish", "website", "Publish content"),
  p("forms.manage", "website", "Manage public forms"),
  p("media.manage", "website", "Manage media assets"),
  p("portal.access", "member_portal", "Use the member portal"),
  p("membership.read", "membership", "View membership records"),
  p("membership.manage", "membership", "Manage pathways and records"),
  p("groups.read", "groups", "View all groups"),
  p("groups.manage", "groups", "Manage all groups"),
  p("groups.read_assigned", "groups", "View assigned groups only"),
  p("groups.manage_assigned", "groups", "Manage assigned groups only"),
  p("serving.manage", "groups", "Manage serve teams"),
  p("events.read", "events", "View events"),
  p("events.manage", "events", "Manage events"),
  p("registrations.manage", "events", "Manage registrations"),
  p("events.check_in", "event_checkin", "Operate check-in"),
  p("services.read", "services", "View service plans"),
  p("services.manage", "services", "Manage plans, teams, positions, schedules"),
  p("services.respond", "services", "Accept or decline serving requests and set availability"),
  p("communications.read", null, "View messages and campaigns"),
  p("communications.compose", null, "Draft messages"),
  p("communications.send", null, "Send messages and campaigns", true),
  p("communications.approve", null, "Approve campaigns"),
  p(
    "communications.inbox",
    null,
    "Read and answer text replies (a conversation marked confidential also needs care.read_confidential)",
    true,
  ),
  p("sms.configure", "sms_messaging", "Configure SMS sender identity"),
  p("email.configure", "email_messaging", "Configure email sender identity"),
  p("commerce.read", "commerce", "View orders and catalog"),
  p("commerce.manage_catalog", "commerce", "Manage products and inventory"),
  p("commerce.fulfill_orders", "commerce", "Fulfill orders"),
  p("commerce.refund_orders", "commerce", "Issue refunds", true),
  p("sermons.manage", "sermons_media", "Manage sermons and media"),
  p("reports.advanced", "advanced_reporting", "Use advanced reports"),
  p("reports.export", "advanced_reporting", "Export report data", true),
  // A campus is a record every church has (ADR 0036), so managing it belongs to tenant administration, a base module.
  // multi_campus gates campus-scoped access control, which is the paid feature, not the existence of the record.
  p("campuses.manage", "tenant_admin", "Manage campuses, their addresses and service times"),
  p("api.manage", "api_access", "Manage API keys", true),
  p("webhooks.manage", "webhooks", "Manage outbound webhooks"),
  p("integrations.manage", "integrations", "Connect and manage integrations", true),
  p("workflows.manage", "custom_workflows", "Manage automations"),
];

export interface RoleTemplate {
  readonly key: RoleKey;
  readonly name: string;
  readonly description: string;
  readonly isPrivileged: boolean;
  readonly permissions: readonly PermissionKey[];
}

const ALL: readonly PermissionKey[] = PERMISSIONS.map((x) => x.key);

/**
 * Owner-only permissions (ADR-0008). tenant_admin is a strict subset of tenant_owner: administrators run
 * day-to-day operations and staff accounts, but cannot change what the tenant *is* (settings, domains, MFA
 * policy, API keys, integrations, webhooks) and cannot move money or bulk-extract people.
 */
const OWNER_ONLY: readonly PermissionKey[] = [
  "settings.manage",
  "settings.time_zone",
  "domains.manage_signin",
  "people.export",
  "api.manage",
  "integrations.manage",
  "webhooks.manage",
  "commerce.refund_orders",
];
const ADMIN: readonly PermissionKey[] = ALL.filter((key) => !OWNER_ONLY.includes(key));

/*
 * `people.read_minors` is held by owners, administrators and ministry leaders, the system roles that include the staff
 * who lead children's ministry. Staff, coordinators and the rest see adults only (ADR 0028).
 *
 * `notes.read_confidential` and `people.import` are deliberately in no system role below owner and administrator.
 * A confidential note is written for a narrower audience than the staff who can read ordinary notes, and a bulk
 * import writes thousands of records at once; neither should arrive by default with a ministry or coordinator role.
 * Both are marked sensitive, so a support session only receives them when they are granted at request time.
 */

const r = (
  key: RoleKey,
  name: string,
  description: string,
  isPrivileged: boolean,
  permissions: readonly PermissionKey[],
): RoleTemplate => ({ key, name, description, isPrivileged, permissions });

/** System role templates seeded for every tenant. Only owners can grant tenant_owner (enforced in SQL). */
export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  r(
    "tenant_owner",
    "Owner",
    "Full control of the church account. Only owners can grant owner.",
    true,
    ALL,
  ),
  r(
    "tenant_admin",
    "Administrator",
    "Broad operational access. Cannot change church settings, domains, MFA policy, API keys, integrations, or issue refunds and exports.",
    true,
    ADMIN,
  ),
  r(
    "staff",
    "Staff",
    "Read-only staff access to people, notes, events, groups, Services, content.",
    false,
    [
      "people.read",
      "notes.read",
      "care.read",
      "events.read",
      "groups.read",
      "services.read",
      "website.read",
      "portal.access",
    ],
  ),
  r(
    "ministry_leader",
    "Ministry leader",
    "Scoped people, groups, events, Services, and follow-up.",
    false,
    [
      "people.read",
      "people.read_minors",
      "people.write",
      "notes.read",
      "notes.write",
      "care.read",
      "care.manage",
      "groups.read_assigned",
      "groups.manage_assigned",
      "events.read",
      "events.manage",
      "registrations.manage",
      "services.read",
      "services.manage",
      "communications.read",
      "communications.compose",
      "portal.access",
    ],
  ),
  r(
    "membership_coordinator",
    "Membership coordinator",
    "Membership workflow administration.",
    false,
    [
      "people.read",
      "people.write",
      "notes.read",
      "notes.write",
      "care.read",
      "care.manage",
      "households.manage",
      "tags.manage",
      "preferences.manage",
      "membership.read",
      "membership.manage",
      "portal.access",
    ],
  ),
  r(
    "communications_manager",
    "Communications manager",
    "Messaging, templates, campaigns, approvals.",
    false,
    [
      "people.read",
      "communications.read",
      "communications.compose",
      "communications.send",
      "communications.approve",
      "communications.inbox",
      "preferences.manage",
      "sms.configure",
      "email.configure",
      "portal.access",
    ],
  ),
  r("store_manager", "Store manager", "Catalog, inventory, fulfillment, refunds.", false, [
    "people.read",
    "commerce.read",
    "commerce.manage_catalog",
    "commerce.fulfill_orders",
    "commerce.refund_orders",
    "portal.access",
  ]),
  r("content_editor", "Content editor", "Website content, forms, media, sermons.", false, [
    "website.read",
    "website.manage",
    "website.publish",
    "forms.manage",
    "media.manage",
    "sermons.manage",
    "portal.access",
  ]),
  r("group_leader", "Group leader", "Assigned groups and serving responses only.", false, [
    "groups.read_assigned",
    "groups.manage_assigned",
    "services.respond",
    "portal.access",
  ]),
  r("volunteer", "Volunteer", "Responds to serving requests.", false, [
    "services.respond",
    "portal.access",
  ]),
  r("member", "Member", "Member portal self-service.", false, ["portal.access"]),
  r("guardian", "Guardian", "Member portal access for a parent or guardian.", false, [
    "portal.access",
  ]),
];
